import type { CSSProperties, ReactNode } from 'react';
import { Button as AriaButton, type ButtonProps as AriaButtonProps } from 'react-aria-components';

export type TagTone = 'neutral' | 'accent' | 'info' | 'success' | 'warning' | 'error';
export type TagVariant = 'soft' | 'solid' | 'outline';
export type TagSize = 'sm' | 'md';

interface TagBaseProps {
  children: ReactNode;
  tone?: TagTone;
  variant?: TagVariant;
  size?: TagSize;
  icon?: ReactNode;
  count?: ReactNode;
  className?: string;
  style?: CSSProperties;
}

export type TagProps = TagBaseProps;
export type TagButtonProps = TagBaseProps &
  Omit<AriaButtonProps, 'children' | 'className' | 'style'>;

const SIZE: Record<TagSize, CSSProperties> = {
  sm: {
    minHeight: 24,
    padding: '2px var(--space-2)',
    gap: 5,
    fontSize: 'var(--type-meta-size)',
  },
  md: {
    minHeight: 32,
    padding: '4px var(--space-3)',
    gap: 6,
    fontSize: 'var(--text-sm)',
  },
};

function toneVars(tone: TagTone) {
  if (tone === 'neutral') {
    return {
      text: 'var(--color-text-secondary)',
      mutedText: 'var(--color-text-muted)',
      bg: 'var(--color-surface-muted)',
      border: 'var(--color-surface-border)',
      solidBg: 'var(--color-text-secondary)',
      solidText: 'var(--color-text-on-accent)',
    };
  }

  return {
    text: `var(--color-${tone}-text)`,
    mutedText: `var(--color-${tone}-text)`,
    bg: `var(--color-${tone}-subtle)`,
    border: `var(--color-${tone}-outline, var(--color-border))`,
    solidBg: `var(--color-${tone})`,
    solidText: 'var(--color-text-on-accent)',
  };
}

function getTagStyle(
  tone: TagTone,
  variant: TagVariant,
  isHovered = false,
  isPressed = false,
  isFocusVisible = false,
  isDisabled = false
): CSSProperties {
  const vars = toneVars(tone);
  const softBg = tone === 'neutral' && isHovered ? 'var(--color-surface-hover)' : vars.bg;
  const outlineBg = isPressed
    ? 'var(--color-surface-press)'
    : isHovered
      ? 'var(--color-surface-hover)'
      : 'transparent';

  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 'fit-content',
    maxWidth: '100%',
    borderRadius: 'var(--radius-full)',
    border:
      variant === 'solid'
        ? '1px solid transparent'
        : `1px solid ${variant === 'outline' ? vars.border : 'transparent'}`,
    color: variant === 'solid' ? vars.solidText : vars.text,
    background: variant === 'solid' ? vars.solidBg : variant === 'outline' ? outlineBg : softBg,
    boxShadow: isFocusVisible ? '0 0 0 3px var(--color-accent-outline)' : 'none',
    fontFamily: 'var(--font-sans)',
    fontWeight: 'var(--weight-medium)',
    lineHeight: 1.35,
    letterSpacing: 'var(--tracking-normal)',
    whiteSpace: 'nowrap',
    opacity: isDisabled ? 0.55 : 1,
    cursor: isDisabled ? 'not-allowed' : undefined,
    transition:
      'background var(--motion-fast) var(--motion-ease-out), border-color var(--motion-fast) var(--motion-ease-out), color var(--motion-fast) var(--motion-ease-out), box-shadow var(--motion-fast) var(--motion-ease-out), transform var(--motion-fast) var(--motion-ease-out)',
    transform: isPressed ? 'scale(0.98)' : undefined,
    ...SIZE.sm,
  };
}

function TagContent({ icon, children, count }: Pick<TagBaseProps, 'icon' | 'children' | 'count'>) {
  return (
    <>
      {icon ? <span className="inline-flex shrink-0 items-center">{icon}</span> : null}
      <span className="truncate">{children}</span>
      {count != null ? <span style={{ opacity: 0.72 }}>{count}</span> : null}
    </>
  );
}

export function Tag({
  children,
  tone = 'neutral',
  variant = 'soft',
  size = 'sm',
  icon,
  count,
  className,
  style,
}: TagProps) {
  return (
    <span
      className={className}
      style={{
        ...getTagStyle(tone, variant),
        ...SIZE[size],
        ...style,
      }}
    >
      <TagContent icon={icon} count={count}>
        {children}
      </TagContent>
    </span>
  );
}

export function TagButton({
  children,
  tone = 'neutral',
  variant = 'soft',
  size = 'sm',
  icon,
  count,
  className,
  style,
  ...props
}: TagButtonProps) {
  return (
    <AriaButton
      {...props}
      className={className}
      style={({ isHovered, isPressed, isFocusVisible, isDisabled }) => ({
        ...getTagStyle(tone, variant, isHovered, isPressed, isFocusVisible, isDisabled),
        ...SIZE[size],
        ...style,
      })}
    >
      <TagContent icon={icon} count={count}>
        {children}
      </TagContent>
    </AriaButton>
  );
}
